import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import config from 'constants/config'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { diffInDays } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { useCallback, useMemo } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalHotelSearchTileFeatures from './ExperimentalHotelSearchTileFeatures'
import {
  GroupedHotelSearchTileOptionCard,
  GroupedHotelSearchTileOptionCardSkeleton,
  GroupedHotelSearchTileUnavailableOptionCard,
} from './GroupedHotelSearchTileOptionCards'
import HotelSearchTileFeatures from './HotelSearchTileFeatures'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'

const SECONDARY_OPTION_CARD_DISPLAY_STATES = {
  hidden: 'hidden',
  loading: 'loading',
  availableForSelectedDates: 'availableForSelectedDates',
  alternativeSuggestedDates: 'alternativeSuggestedDates',
  unavailable: 'unavailable',
} as const

interface SecondaryOptionCardDisplayStateProps {
  secondaryOffer?: App.HotelOffer | App.HotelOfferSummary
  secondaryOfferMetaData?: App.OfferListMetaData
  selectedCheckInDate?: string
  selectedCheckOutDate?: string
  secondarySuggestedCheckInDate?: string
  secondarySuggestedCheckOutDate?: string
  fetchingSecondaryOffer: boolean
}

type SecondaryOptionCardDisplayState = typeof SECONDARY_OPTION_CARD_DISPLAY_STATES[keyof typeof SECONDARY_OPTION_CARD_DISPLAY_STATES]

interface Props {
  filters?: App.OfferListFilters
  groupedItemType?: App.OfferListGroupedItemType
  bundledOfferId?: string
  secondaryOffer?: App.HotelOffer | App.HotelOfferSummary
  secondaryOfferMetaData?: App.OfferListMetaData
  fetchingSecondaryOffer: boolean
  noGoogleAffiliations: boolean
  maxTotalInclusions: number
  showSecondaryLuxPlusPromos: boolean
  hidePrice?: boolean
  showPricePoint?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
  useCompactPricing?: boolean
  doNotOpenInclusionsModal?: boolean
  fallbackDuration?: number
  onClick?: (event: MouseEvent<HTMLElement>) => void
  productClick?: (offer: App.AnyOffer) => void
}

function getDateOnly(date?: string) {
  return date?.split('T')[0]
}

function getSecondaryOptionCardDisplayState({
  secondaryOffer,
  secondaryOfferMetaData,
  selectedCheckInDate,
  selectedCheckOutDate,
  secondarySuggestedCheckInDate,
  secondarySuggestedCheckOutDate,
  fetchingSecondaryOffer,
}: SecondaryOptionCardDisplayStateProps): SecondaryOptionCardDisplayState {
  if (fetchingSecondaryOffer) return SECONDARY_OPTION_CARD_DISPLAY_STATES.loading
  if (!secondaryOffer) return SECONDARY_OPTION_CARD_DISPLAY_STATES.hidden

  const hasSelectedCheckInDate = !!selectedCheckInDate
  const hasSecondarySuggestedCheckInDate = !!secondarySuggestedCheckInDate

  if (!hasSelectedCheckInDate || !hasSecondarySuggestedCheckInDate) {
    if (secondaryOfferMetaData?.available === false) return SECONDARY_OPTION_CARD_DISPLAY_STATES.unavailable
    return SECONDARY_OPTION_CARD_DISPLAY_STATES.availableForSelectedDates
  }

  if (selectedCheckInDate === secondarySuggestedCheckInDate && selectedCheckOutDate === secondarySuggestedCheckOutDate) {
    return SECONDARY_OPTION_CARD_DISPLAY_STATES.availableForSelectedDates
  }

  if (secondaryOfferMetaData?.suggestedTravelDates?.price) return SECONDARY_OPTION_CARD_DISPLAY_STATES.alternativeSuggestedDates
  return SECONDARY_OPTION_CARD_DISPLAY_STATES.unavailable
}

function GroupedHotelSearchTileSecondaryOptionCard({
  filters,
  groupedItemType,
  bundledOfferId,
  secondaryOffer,
  secondaryOfferMetaData,
  fetchingSecondaryOffer,
  noGoogleAffiliations,
  maxTotalInclusions,
  showSecondaryLuxPlusPromos,
  hidePrice = false,
  showPricePoint = true,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
  useCompactPricing = false,
  doNotOpenInclusionsModal = false,
  fallbackDuration,
  onClick,
  productClick = noop,
}: Props) {
  const intl = useIntl()
  const isAlternativeGroupedTile = groupedItemType === 'alternative'
  const secondarySuggestedTravelDates = secondaryOfferMetaData?.suggestedTravelDates
  const selectedCheckInDate = getDateOnly(filters?.checkIn)
  const selectedCheckOutDate = getDateOnly(filters?.checkOut)
  const selectedDuration = selectedCheckInDate && selectedCheckOutDate ?
      diffInDays(new Date(selectedCheckInDate), new Date(selectedCheckOutDate)) :
    undefined
  const secondarySuggestedCheckInDate = getDateOnly(secondarySuggestedTravelDates?.checkIn)
  const secondarySuggestedCheckOutDate = getDateOnly(secondarySuggestedTravelDates?.checkOut)
  const secondaryOptionCardDisplayState = getSecondaryOptionCardDisplayState({
    secondaryOffer,
    secondaryOfferMetaData,
    selectedCheckInDate,
    selectedCheckOutDate,
    secondarySuggestedCheckInDate,
    secondarySuggestedCheckOutDate,
    fetchingSecondaryOffer,
  })
  const showSecondaryAlternativeDates = secondaryOptionCardDisplayState === SECONDARY_OPTION_CARD_DISPLAY_STATES.alternativeSuggestedDates
  const showSecondaryUnavailable = showSecondaryAlternativeDates || secondaryOptionCardDisplayState === SECONDARY_OPTION_CARD_DISPLAY_STATES.unavailable
  const isSecondaryAvailableForSelectedDates = isAlternativeGroupedTile &&
    secondaryOptionCardDisplayState === SECONDARY_OPTION_CARD_DISPLAY_STATES.availableForSelectedDates
  const secondaryOfferUrl = useOfferUrl(secondaryOffer, {
    filters,
    bundledOfferId,
    offerLinkIncludesFilters: true,
    showDatePicker: (isAlternativeGroupedTile && !isSecondaryAvailableForSelectedDates) || hidePrice,
  })
  const offerLinkTarget = config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined

  const secondaryBestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!secondaryOffer) return undefined

    const secondaryPackageId = secondaryOfferMetaData?.pricing.lowestPricePackageId ?? secondaryOffer.lowestPricePackage?.id
    const secondaryDuration = secondaryOfferMetaData?.pricing.duration ?? secondaryOffer.lowestPricePackage?.duration
    const secondaryRoomRateId = secondaryOfferMetaData?.pricing.lowestPriceRoomRateId ?? secondaryOffer.lowestPricePackage?.roomRate?.id

    if (!secondaryPackageId || !secondaryDuration || !secondaryRoomRateId) return secondaryOffer.lowestPricePackage

    return secondaryOffer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(secondaryPackageId, secondaryDuration, secondaryRoomRateId)) ?? secondaryOffer.lowestPricePackage
  }, [secondaryOffer, secondaryOfferMetaData])

  const isSecondaryUnavailableDueToMinDuration = isAlternativeGroupedTile &&
    secondaryOfferMetaData?.available === false &&
    !!selectedDuration &&
    !!secondaryOffer?.minDuration &&
    secondaryOffer.minDuration > selectedDuration
  const secondaryTitle = (() => {
    if (!isAlternativeGroupedTile) return intl.formatMessage(flightBundleGlossaryMessages.hotelAndFlightBundle)

    if (isSecondaryAvailableForSelectedDates) {
      return intl.formatMessage({
        id: '9jH0vVPiS_FpfyNqvK2O',
        defaultMessage: 'Available for your dates',
        description: 'Title for the available primary option card in an alternative grouped hotel search tile',
      })
    }

    if (isSecondaryUnavailableDueToMinDuration && secondaryOffer?.minDuration) {
      return intl.formatMessage(
        {
          id: 'sdn5Gcir8TzZtaUoqa_c',
          defaultMessage: 'Extend to {minNumNights, plural, one {# night} other {# nights}} to unlock this amazing offer',
          description: 'Headline for the secondary flash offer card when the selected stay is shorter than the minimum stay required',
        },
        {
          minNumNights: secondaryOffer.minDuration,
        },
      )
    }

    return intl.formatMessage({
      id: 'iHxjSLQLZEm6K_qUE-Tm',
      defaultMessage: 'Change to nearby dates to unlock this amazing offer',
      description: 'Headline for the secondary flash offer card when nearby dates are needed to unlock the alternative offer',
    })
  })()
  const secondaryActionLabel = (() => {
    if (isSecondaryAvailableForSelectedDates) {
      return intl.formatMessage({
        id: 'wgbxIJoadg0wltSWEp8d',
        defaultMessage: 'View offer',
        description: 'Primary option card CTA label in the grouped hotel search result tile',
      })
    }

    if (isAlternativeGroupedTile) {
      return intl.formatMessage({
        id: 'S1DiEPUXUTf_PaerWUIw',
        defaultMessage: 'View available dates',
        description: 'Secondary option card CTA label for opening an alternative flash offer date picker',
      })
    }

    return intl.formatMessage({
      id: 's6jgxm2CNTlRUr6pZMcc',
      defaultMessage: 'View bundle',
      description: 'Secondary option card CTA label in the grouped hotel search result tile',
    })
  })()
  const secondaryUnavailableLabel = intl.formatMessage({
    id: 'VrVKcZrNGwZLOWqsOToe',
    defaultMessage: 'Unavailable for selected dates',
    description: 'Unavailable state message for the secondary option card in the grouped hotel search result tile',
  })
  const secondarySuggestedDuration = secondarySuggestedTravelDates?.checkIn && secondarySuggestedTravelDates.checkOut ?
      diffInDays(new Date(secondarySuggestedTravelDates.checkIn), new Date(secondarySuggestedTravelDates.checkOut)) :
    undefined
  const secondarySuggestedCheckInLabel = secondarySuggestedTravelDates?.checkIn ? intl.formatDate(new Date(secondarySuggestedTravelDates.checkIn), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }) : undefined
  const secondaryDepartureLabel = showSecondaryAlternativeDates && secondarySuggestedCheckInLabel && secondarySuggestedDuration ? intl.formatMessage({
    id: '1IcutQKGS1IDSDJc2ZG0',
    defaultMessage: 'Check-in {checkInDate} for {nights, plural, one {# night} other {# nights}} from',
    description: 'Alternative check-in date and duration message for the secondary option card in the grouped hotel search result tile',
  }, {
    checkInDate: secondarySuggestedCheckInLabel,
    nights: secondarySuggestedDuration,
  }) : undefined
  const secondaryPriceUnitLabel = intl.formatMessage({
    id: 'j8ECXusbFUfaCMcHW-pF',
    defaultMessage: 'total',
    description: 'Sale unit label for the secondary unavailable option card price in the grouped hotel search result tile',
  })
  const secondarySuggestedMemberPrice = secondarySuggestedTravelDates?.memberPrice && secondarySuggestedTravelDates.memberPrice > 0 ?
    secondarySuggestedTravelDates.memberPrice :
    undefined
  const secondarySuggestedPrice = showSecondaryAlternativeDates && !secondaryOfferMetaData?.hidePricing ?
    secondarySuggestedMemberPrice ?? secondarySuggestedTravelDates?.price :
    undefined
  const secondaryFeatures = secondaryOffer ? (
    !noGoogleAffiliations ?
        <HotelSearchTileFeatures
          offer={secondaryOffer}
          offerMetaData={secondaryOfferMetaData}
          filters={filters}
          bestPackage={secondaryBestPackage}
          doNotOpenInclusionsModal={doNotOpenInclusionsModal}
          maxTotalInclusions={maxTotalInclusions}
          hideFeatureTitle
          hideRoomBreakdown
        /> :
        <ExperimentalHotelSearchTileFeatures
          offer={secondaryOffer}
          offerMetaData={secondaryOfferMetaData}
          filters={filters}
          bestPackage={secondaryBestPackage}
          maxInclusionsToShow={maxTotalInclusions}
          hideSecondaryLuxPlusPromos={!showSecondaryLuxPlusPromos}
          hideLuxPlusLabels
          hideUpgradableInclusionsLabel
          hideRoomBreakdown
        />
  ) : false
  const secondaryPricePoint = secondaryOffer && showPricePoint && <HotelSearchTilePriceStack
    offer={secondaryOffer}
    filters={filters}
    bestPackage={secondaryBestPackage}
    duration={secondaryOfferMetaData?.pricing.duration ?? fallbackDuration ?? 1}
    hidePrice={hidePrice}
    showPerNightPricing={showPerNightPricing}
    hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
    disableCharterPricing
    showCompactPricing={useCompactPricing}
  />
  const secondaryProductLabel = isAlternativeGroupedTile && secondaryOffer ? <ProductTypeLabel productType={secondaryOffer.productType} /> : undefined
  const handleSecondaryOptionCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    onClick?.(event)
    if (secondaryOffer) {
      productClick?.(secondaryOffer)
    }
  }, [onClick, productClick, secondaryOffer])

  if (fetchingSecondaryOffer && isAlternativeGroupedTile) {
    return <GroupedHotelSearchTileOptionCardSkeleton />
  }
  if (!isAlternativeGroupedTile && secondaryOptionCardDisplayState === SECONDARY_OPTION_CARD_DISPLAY_STATES.loading) {
    return <GroupedHotelSearchTileOptionCardSkeleton />
  }
  if (secondaryOffer && isAlternativeGroupedTile) {
    return <GroupedHotelSearchTileOptionCard
      title={secondaryTitle}
      features={secondaryFeatures}
      pricePoint={secondaryPricePoint}
      actionLabel={secondaryActionLabel}
      label={secondaryProductLabel}
      variant="highlighted"
      to={secondaryOfferUrl}
      target={offerLinkTarget}
      onClick={handleSecondaryOptionCardClick}
    />
  }
  if (secondaryOffer && !isAlternativeGroupedTile && secondaryOptionCardDisplayState === SECONDARY_OPTION_CARD_DISPLAY_STATES.availableForSelectedDates) {
    return <GroupedHotelSearchTileOptionCard
      title={secondaryTitle}
      features={secondaryFeatures}
      pricePoint={secondaryPricePoint}
      actionLabel={secondaryActionLabel}
      to={secondaryOfferUrl}
      target={offerLinkTarget}
      onClick={handleSecondaryOptionCardClick}
    />
  }
  if (secondaryOffer && !isAlternativeGroupedTile && showSecondaryUnavailable) {
    return <GroupedHotelSearchTileUnavailableOptionCard
      title={secondaryTitle}
      features={secondaryFeatures}
      unavailableLabel={secondaryUnavailableLabel}
      departureLabel={showSecondaryAlternativeDates ? secondaryDepartureLabel : undefined}
      price={secondarySuggestedPrice}
      priceUnitLabel={secondaryPriceUnitLabel}
      actionLabel={secondaryActionLabel}
      to={secondaryOfferUrl}
      target={offerLinkTarget}
      onClick={handleSecondaryOptionCardClick}
    />
  }

  return null
}

export default GroupedHotelSearchTileSecondaryOptionCard
